import React from 'react';
import {  Redirect, Route, Switch } from 'react-router-dom';

import Dashboard from '../components/dashboard/Dashboard';
import AddStockPage from '../components/AddStockPage';
import EditStockPage from '../components/EditStockPage';
import NotFoundPage from '../components/NotFoundPage';
import Header from '../components/Header';
import { FaSpinner } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import SystemSettingsPage from '../components/SystemSettingsPage';
import FileCleanupPage from '../components/systemSettings/FileCleanupPage';
import ReportPage from '../components/report/ReportPage';
import styled from 'styled-components'
import XeroPage from '../components/XeroPage';
import NewOrderPage from '../components/servicesOrderForm/NewOrderPage';
import ServiceOrdersPage from '../components/servicesOrderForm/ServiceOrdersPage';
import ProtectedRoute, { ProtectedRouteProps } from './ProtectedRoute';
import Login from '../components/Login';
import { IGlobalState } from '../model/IGlobalState';
import AnnouncementCommentResponsePage from '../components/announcementCommentResponse/AnnouncementCommentResponsePage';
import ClientDashboard from '../components/client/ClientDashboard';
import UsersPage from '../components/users/UsersPage';
import BillableStocksPage from '../components/BillableStocksPage';
import UserPage from '../components/users/UserPage';
import ClientStockItem from '../components/client/ClientStockItem';
import { HasAdminRole, HasRole, V2 } from '../model/Roles';
import HeaderV2 from '../components/v2/Header';
import DashboardV2 from '../components/v2/Dashboard';
import AnalyticsV2 from '../components/v2/Analytics';
import ShareRegistryV2 from '../components/v2/shareRegistry/ShareRegistry';
import CommunicationsV2 from '../components/v2/communications/Communications';
import AccountV2 from '../components/v2/account/Account';
import NotificationsV2 from '../components/v2/notifications/Notifications';
import HelpCentreV2 from '../components/v2/help/HelpCentre';
import Toaster from '../components/v2/Toast';
import TopProgress from '../components/v2/TopProgress';
import EditAnnouncementComments from '../components/client/EditAnnouncementComments';
import CampaignStatsComponent from '../components/campaignStats/CampaignStatsComponent';
import EditAnnouncementCampaignPage from '../components/EditAnnouncementCampaignPage';
import SendWelcomeEmailPage from '../components/SendWelcomeEmailPage';
import ResetPasswordPage from '../components/ResetPasswordPage';
import ForgotPasswordPage from '../components/ForgotPasswordPage';




const Style = styled.div`
max-width:1250px;
margin:auto;
&.v2 { max-width:none; min-height:100vh; background:#f4f6fa; }
// My profile "Text size" — v2 sizes are px, so scale the whole frame
&.v2.text-large { zoom:1.1; }
&.v2.text-extra-large { zoom:1.2; }
// v1 pages opened from the v2 dashboard sit in the same frame as .v2-page
&.v2 > :not(.v2-header):not(.v2-page):not(.loading):not(.v2-toasts):not(.v2-top-progress):not(.login-page) { padding:28px 40px 60px; max-width:1440px; margin:0 auto; box-sizing:border-box; }
`;

const publicRoutes = [ '/report', '/new-order', '/login', '/forgot-password', '/reset-password']

const AppRouter:React.FC = () => {
    const loading = useSelector((state:IGlobalState)=>state.loading);
    const user = useSelector((state:IGlobalState)=>state.user);
    const isAuthenticated = !!user.username;
    const routerProps: ProtectedRouteProps = {
      isAuthenticated,
        authenticationPath: '/login'        
      };
    
    const showHeader = isAuthenticated && publicRoutes.every(x=>!window.location.pathname.startsWith(x))

    const isAdmin = isAuthenticated && HasAdminRole(user)
    const isV2 = isAuthenticated && HasRole(user, V2)

    const isPublicRoute = publicRoutes.some(x=>window.location.pathname.startsWith(x))
    if (isAuthenticated && user.passwordResetRequired && !isPublicRoute) {
        return <ResetPasswordPage />
    }

    return <Style className={isV2 ? `v2 text-${user.textSize ?? 'standard'}` : ''}>

            { showHeader && (isV2 ? <HeaderV2 /> : <Header />)}
            { isV2 && <><TopProgress /><Toaster /></> }

            {/* v2 shows TopProgress for every request instead of this v1 spinner (startSetStock sets it on each page load) */}
            {loading && !isV2 && <div className="loading"><FaSpinner className="fa-spin" size={32} color="grey" /></div>}

            
              <Switch>

              {isV2 && <Redirect exact from='/' to='/dashboard/overview' />}
              <ProtectedRoute {...routerProps} path='/dashboard/overview' component={isV2 ? DashboardV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/dashboard/analytics' component={isV2 ? AnalyticsV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/share-registry/:tab?/:id?' component={isV2 ? ShareRegistryV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/communications/:tab?/:id?/:view?' component={isV2 ? CommunicationsV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/account/:tab?' component={isV2 ? AccountV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/notifications/:tab?' component={isV2 ? NotificationsV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/help' component={isV2 ? HelpCentreV2 : NotFoundPage} />
              <ProtectedRoute {...routerProps} path='/' component={isAdmin ? Dashboard : ClientDashboard} exact={true}/>
              
              <Route path="/report/:id" component={ReportPage} />                
                    
              <Route path="/new-order/:id?" component={NewOrderPage} />
              <Route path="/login" component={Login} />
              <Route path="/forgot-password" component={ForgotPasswordPage} />
              <Route path="/reset-password" component={ResetPasswordPage} />

              <ProtectedRoute {...routerProps} path="/create" component={AddStockPage} />
              <ProtectedRoute {...routerProps} path="/stocks/:id/:tab?/:subtab?" component={EditStockPage} />
              <ProtectedRoute {...routerProps} path="/system-settings/file-cleanup" component={FileCleanupPage} />
              <ProtectedRoute {...routerProps} path="/system-settings" component={SystemSettingsPage} />
              <ProtectedRoute {...routerProps} path="/xero-page" component={XeroPage} />
              <ProtectedRoute {...routerProps} path="/orders" component={ServiceOrdersPage} />
              <ProtectedRoute {...routerProps} path="/billable-stocks" component={BillableStocksPage} exact={true}/>
              <ProtectedRoute {...routerProps} path="/users" component={UsersPage} exact={true}/>
              <ProtectedRoute {...routerProps} path="/users/:id" component={UserPage} />

              <ProtectedRoute {...routerProps} path="/AnnouncementCommentResponse/:id" component={AnnouncementCommentResponsePage} />          
              {isAdmin && <Route path="/client-stocks/" render={({location}) =>
                  <Redirect to={location.pathname.replace('/client-stocks/', '/stocks/') + location.search} />} />}
              <ProtectedRoute {...routerProps} path="/client-stocks/:combinedTicker/:tab?/:subtab?" component={ClientStockItem} />
              <ProtectedRoute {...routerProps} path="/AnnouncementComments/:combinedTicker/:announcementGuid?" component={EditAnnouncementComments} />
              <ProtectedRoute {...routerProps} path="/CampaignStats/:campaignId" component={CampaignStatsComponent} />
              <ProtectedRoute {...routerProps} path="/EditAnnouncementCampaign/:combinedTicker/:announcementGuid" component={EditAnnouncementCampaignPage} />
              <ProtectedRoute {...routerProps} path="/SendWelcomeEmail/:combinedTicker/:holderId" component={SendWelcomeEmailPage} />


              <Route component={NotFoundPage} />
              
              </Switch>
           
        
    </Style>
}


 export default AppRouter
